import { FilterDefs, PageHeaderLayout, treeFilter, useGridTableLayoutState } from "@homebound/beam";
import { useCallback, useMemo } from "react";
import { createMilestoneCatalogFormUrl, createMilestoneCatalogUrl } from "src/RouteUrls";
import { GlobalPlanMilestoneFilter, Order } from "src/generated/graphqlTypes";
import {
  MilestoneCatalogPageMetaDataQuery,
  useMilestoneCatalogPageMetaDataQuery,
  useMilestoneCatalogPageQuery,
} from "src/routes/libraries/milestone-catalog/MilestoneCatalogPage.generated";
import { queryResult } from "src/utils";
import { formatTreeSelectOptions } from "./MilestoneCatalogForm";
import { MilestoneCatalogTable } from "./MilestoneCatalogTable";

export function MilestoneCatalogPage() {
  const query = useMilestoneCatalogPageMetaDataQuery();

  return queryResult(query, (data) => <MilestoneCatalogPageView data={data} />);
}

type MilestoneCatalogPageViewProps = { data: MilestoneCatalogPageMetaDataQuery };

function MilestoneCatalogPageView({ data }: MilestoneCatalogPageViewProps) {
  const { globalPlanMilestoneGroups } = data ?? { globalPlanMilestoneGroups: [] };

  const filterDefs: FilterDefs<GlobalPlanMilestoneFilter> = useMemo(() => {
    const treeSelectOptions = formatTreeSelectOptions(globalPlanMilestoneGroups);

    return {
      groups: treeFilter({
        label: "Groups",
        filterBy: "leaf",
        getOptionLabel: (o) => o.name,
        getOptionValue: (o) => o.id,
        options: treeSelectOptions,
      }),
    };
  }, [globalPlanMilestoneGroups]);

  const layoutState = useGridTableLayoutState<GlobalPlanMilestoneFilter>({
    persistedFilter: { storageKey: "globalPlanMilestoneFilter", filterDefs },
    search: "server",
  });

  const query = useMilestoneCatalogPageQuery({
    variables: {
      filter: { name: layoutState.searchString, ...layoutState.filter },
      page: { offset: 0, limit: 100 },
      orderBy: { createdAt: Order.Desc },
    },
  });

  const maybeFetchMore = useCallback(async () => {
    if (query.data?.globalPlanMilestones.pageInfo.hasNextPage) {
      await query.fetchMore({
        variables: { page: { offset: query.data?.globalPlanMilestones.entities.length, limit: 100 } },
      });
    }
  }, [query]);

  return (
    <PageHeaderLayout
      pageHeader={{
        title: "Milestone Catalog",
        breadcrumbs: { breadcrumbs: [{ href: createMilestoneCatalogUrl(), label: "Milestone Catalog" }] },
        actions: [{ label: "Create New", onClick: createMilestoneCatalogFormUrl() }],
      }}
    >
      {queryResult(query, ({ globalPlanMilestones }) => (
        <MilestoneCatalogTable
          globalPlanMilestones={globalPlanMilestones.entities}
          layoutState={layoutState}
          maybeFetchMore={maybeFetchMore}
        />
      ))}
    </PageHeaderLayout>
  );
}
